import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest } from "../utils/utils";
import {
  IDiseaseCategory,
  IDiseaseDetails,
  IIamInRussia,
  IUsefullLinks,
  IContactLink,
  ITestSection,
  ITestProgress,
  ITestResults,
  ICheckup,
  ICountries,
  ICountryDetails,
} from "../types";

interface IAuthState {
  inRussiaInfo: IIamInRussia | null;
  inKazahkstanInfo: IIamInRussia | null;
  inOtherCountriesInfo: IIamInRussia | null;
  diseaseList: IDiseaseCategory[] | null;
  kazakhstanDiseaseList: IDiseaseCategory[] | null;
  otherCountriesDeportList: IDiseaseCategory[] | null;
  preDepartureDiseasesList: IDiseaseCategory[] | null;
  diseaseDetails: IDiseaseDetails | null;
  kazakhstanDiseaseDetails: IDiseaseDetails | null;
  preDepartureDiseaseDetails: IDiseaseDetails | null;
  importantToKnowList: IDiseaseCategory[] | null;
  importantToKnowDetails: IDiseaseDetails | null;
  kazakhstanTripPreparationDetails: IDiseaseDetails | null;
  russiaHiVMigrantsList: IDiseaseCategory[] | null;
  russiaHiVMigrantsDetails: IDiseaseDetails | null;
  russiaHiVMigrantsTreatmentList: IDiseaseCategory[] | null;
  russiaHiVMigrantsTreatmentDetails: IDiseaseDetails | null;
  moreAboutDiseses: IDiseaseDetails[] | null;
  links: IUsefullLinks[] | null;
  contactLinks: IContactLink[] | null;
  kazakhstanTripPreperaionList: IDiseaseCategory[] | null;
  kazakhstanRemoteMigrationList: IDiseaseCategory[] | null;
  kazakhstanRemoteMigrationDetails: IDiseaseDetails | null;
  testSections: ITestSection[] | null;
  testProgress: ITestProgress | null;
  testResults: ITestResults | null;
  checkupList: ICheckup[] | null;
  otherCountriesDeportationDetails: IDiseaseDetails | null;
  countries: ICountries[] | null;
  countryDetails: ICountryDetails | null;
  lang: "ru" | "kg" | "uz" | "tj";
}

const initialState: IAuthState = {
  inRussiaInfo: null,
  inKazahkstanInfo: null,
  inOtherCountriesInfo: null,
  diseaseList: null,
  kazakhstanDiseaseList: null,
  diseaseDetails: null,
  kazakhstanDiseaseDetails: null,
  preDepartureDiseasesList: null,
  preDepartureDiseaseDetails: null,
  importantToKnowList: null,
  importantToKnowDetails: null,
  russiaHiVMigrantsList: null,
  russiaHiVMigrantsDetails: null,
  russiaHiVMigrantsTreatmentList: null,
  russiaHiVMigrantsTreatmentDetails: null,
  moreAboutDiseses: null,
  links: null,
  contactLinks: null,
  kazakhstanTripPreperaionList: null,
  kazakhstanTripPreparationDetails: null,
  kazakhstanRemoteMigrationList: null,
  kazakhstanRemoteMigrationDetails: null,
  testSections: null,
  testProgress: null,
  testResults: null,
  checkupList: null,
  otherCountriesDeportList: null,
  otherCountriesDeportationDetails: null,
  countries: null,
  countryDetails: null,
  lang: "ru",
};

export const getIamInRussia = createAsyncThunk<
  IIamInRussia,
  void,
  { rejectValue: { error: string } }
>("main/getIamInRussia", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`api/v1/i-am-in-russia/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getIamInKazahkstan = createAsyncThunk<
  IIamInRussia,
  void,
  { rejectValue: { error: string } }
>("main/getIamInKazahkstan", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/i-am-in-kazakhstan/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getIamInOtherCountries = createAsyncThunk<
  IIamInRussia,
  void,
  { rejectValue: { error: string } }
>("main/getIamInOtherCountries", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/in-other-country/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getDiseaseList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getDiseaseCategories", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/diseases/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanDiseaseList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getKazakhstanDiseaseCategories", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/kz-diseases/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getOtherCountriesDeportList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getOtherCountriesDeportList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/other-deport-hiv/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getOtherCountriesDeportationDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>(
  "main/etOtherCountriesDeportationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await publicRequest.get(
        `/api/v1/other-deport-hiv/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getPreDepartureDiseasesList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getPreDepartureDiseasesList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/pretrip-diseases/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getDiseaseDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>("main/getDiseaseDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/diseases/${id}/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanDiseaseDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>("main/getKazakhstanDiseaseDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`api/v1/kz-diseases/${id}/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getPreDepartureDiseaseDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>("main/getPreDepartureDiseaseDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/pretrip-diseases/${id}/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getImportantToKnowList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getImportantToKnowList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/what-is-important-to-know/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanTripPreperaionList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getKazakhstanTripPreperaionList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/trip-preparation/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getImportantToKnowDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>("main/getImportantToKnowDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/what-is-important-to-know/${id}/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanTripPreparationDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>(
  "main/getKazakhstanTripPreparationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await publicRequest.get(
        `/api/v1/trip-preparation/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getRussiaHiVMigrantsList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getRussiaHiVMigrantsList ", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/info-hiv-migrants-russia/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getRussiaHiVMigrantsDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>("main/getRussiaHiVMigrantsDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/info-hiv-migrants-russia/${id}/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanRemoteMigrationList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getKazakhstanRemoteMigrationList", async (id, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/already-in-migration/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKazakhstanRemoteMigrationDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>(
  "main/getKazakhstanRemoteMigrationDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await publicRequest.get(
        `/api/v1/already-in-migration/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getRussiaHiVMigrantsTreatmentList = createAsyncThunk<
  IDiseaseCategory[],
  void,
  { rejectValue: { error: string } }
>("main/getRussiaHiVMigrantsTreatmentList ", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/how-to-tratment-during-migrations/`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getMoreAboutDiseses = createAsyncThunk<
  IDiseaseDetails[],
  void,
  { rejectValue: { error: string } }
>("main/getMoreAboutDiseses", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/hiv-tuberculosis/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getRussiaHiVMigrantsTreatmentDetails = createAsyncThunk<
  IDiseaseDetails,
  number,
  { rejectValue: { error: string } }
>(
  "main/getRussiaHiVMigrantsTreatmentDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await publicRequest.get(
        `/api/v1/how-to-tratment-during-migrations/${id}/`,
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  },
);

export const getUsefullLinks = createAsyncThunk<
  IUsefullLinks[],
  void,
  { rejectValue: { error: string } }
>("main/getUsefullLinks", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/useful-links/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getContactLinks = createAsyncThunk<
  IContactLink[],
  void,
  { rejectValue: { error: string } }
>("main/getContactLinks", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/contact-links/`);
    return data?.results ?? [];
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

// Test API Actions
export const getTestSections = createAsyncThunk<
  ITestSection[],
  void,
  { rejectValue: { error: string } }
>("test/getTestSections", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/test/sections/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getTestProgress = createAsyncThunk<
  ITestProgress,
  void,
  { rejectValue: { error: string } }
>("test/getTestProgress", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/test/progress/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const submitTestAnswer = createAsyncThunk<
  void,
  { question_id: number; answer_option_id: number },
  { rejectValue: { error: string } }
>("test/submitTestAnswer", async (payload, { rejectWithValue }) => {
  try {
    await publicRequest.post(`/api/v1/test/submit-answer/`, payload);
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getTestResults = createAsyncThunk<
  ITestResults,
  void,
  { rejectValue: { error: string } }
>("test/getTestResults", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/test/results/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const completeTest = createAsyncThunk<
  { message: string; session_id: number },
  void,
  { rejectValue: { error: string } }
>("test/completeTest", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(`/api/v1/test/complete/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const resetTest = createAsyncThunk<
  { message: string; session_id: number },
  void,
  { rejectValue: { error: string } }
>("test/resetTest", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(`/api/v1/test/reset/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getTajikistanCheckupList = createAsyncThunk<
  ICheckup[],
  void,
  { rejectValue: { error: string } }
>("main/getTajikistanCheckupList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/checkup-country/tajikistan`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getKyrgyzstanCheckupList = createAsyncThunk<
  ICheckup[],
  void,
  { rejectValue: { error: string } }
>("main/getKyrgyzstanCheckupList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/checkup-country/kyrgyzstan`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getUzbekistanCheckupList = createAsyncThunk<
  ICheckup[],
  void,
  { rejectValue: { error: string } }
>("main/getUzbekistanCheckupList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(
      `/api/v1/checkup-country/uzbekistan`,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getCountriesList = createAsyncThunk<
  ICountries[],
  void,
  { rejectValue: { error: string } }
>("main/getCountriesList", async (_, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.get(`/api/v1/country-restrictions/`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getCountryDetails = createAsyncThunk<
  ICountryDetails,
  { country_id: number },
  { rejectValue: { error: string } }
>("main/getCountryDetails", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `/api/v1/country-restrictions/get-info/`,
      params,
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const infoSlice = createSlice({
  name: "info",
  initialState,
  reducers: {
    setLang(state, action) {
      state.lang = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getIamInRussia.fulfilled, (state, action) => {
        state.inRussiaInfo = action.payload;
      })
      .addCase(getIamInKazahkstan.fulfilled, (state, action) => {
        state.inKazahkstanInfo = action.payload;
      })
      .addCase(getIamInOtherCountries.fulfilled, (state, action) => {
        state.inOtherCountriesInfo = action.payload;
      })
      .addCase(getDiseaseList.fulfilled, (state, action) => {
        state.diseaseList = action.payload;
      })
      .addCase(getKazakhstanDiseaseList.fulfilled, (state, action) => {
        state.kazakhstanDiseaseList = action.payload;
      })
      .addCase(getOtherCountriesDeportList.fulfilled, (state, action) => {
        state.otherCountriesDeportList = action.payload;
      })
      .addCase(
        getOtherCountriesDeportationDetails.fulfilled,
        (state, action) => {
          state.otherCountriesDeportationDetails = action.payload;
        },
      )

      .addCase(getPreDepartureDiseasesList.fulfilled, (state, action) => {
        state.preDepartureDiseasesList = action.payload;
      })
      .addCase(getDiseaseDetails.fulfilled, (state, action) => {
        state.diseaseDetails = action.payload;
      })
      .addCase(getKazakhstanDiseaseDetails.fulfilled, (state, action) => {
        state.kazakhstanDiseaseDetails = action.payload;
      })
      .addCase(getPreDepartureDiseaseDetails.fulfilled, (state, action) => {
        state.preDepartureDiseaseDetails = action.payload;
      })
      .addCase(getImportantToKnowList.fulfilled, (state, action) => {
        state.importantToKnowList = action.payload;
      })
      .addCase(getKazakhstanTripPreperaionList.fulfilled, (state, action) => {
        state.kazakhstanTripPreperaionList = action.payload;
      })
      .addCase(
        getKazakhstanTripPreparationDetails.fulfilled,
        (state, action) => {
          state.kazakhstanTripPreparationDetails = action.payload;
        },
      )
      .addCase(getImportantToKnowDetails.fulfilled, (state, action) => {
        state.importantToKnowDetails = action.payload;
      })
      .addCase(getRussiaHiVMigrantsList.fulfilled, (state, action) => {
        state.russiaHiVMigrantsList = action.payload;
      })
      .addCase(getRussiaHiVMigrantsDetails.fulfilled, (state, action) => {
        state.russiaHiVMigrantsDetails = action.payload;
      })
      .addCase(getRussiaHiVMigrantsTreatmentList.fulfilled, (state, action) => {
        state.russiaHiVMigrantsTreatmentList = action.payload;
      })
      .addCase(
        getRussiaHiVMigrantsTreatmentDetails.fulfilled,
        (state, action) => {
          state.russiaHiVMigrantsTreatmentDetails = action.payload;
        },
      )
      .addCase(getMoreAboutDiseses.fulfilled, (state, action) => {
        state.moreAboutDiseses = action.payload;
      })
      .addCase(getUsefullLinks.fulfilled, (state, action) => {
        state.links = action.payload;
      })
      .addCase(getContactLinks.fulfilled, (state, action) => {
        state.contactLinks = action.payload;
      })
      .addCase(getKazakhstanRemoteMigrationList.fulfilled, (state, action) => {
        state.kazakhstanRemoteMigrationList = action.payload;
      })
      .addCase(
        getKazakhstanRemoteMigrationDetails.fulfilled,
        (state, action) => {
          state.kazakhstanRemoteMigrationDetails = action.payload;
        },
      )
      .addCase(getTajikistanCheckupList.fulfilled, (state, action) => {
        state.checkupList = action.payload;
      })
      .addCase(getKyrgyzstanCheckupList.fulfilled, (state, action) => {
        state.checkupList = action.payload;
      })
      .addCase(getUzbekistanCheckupList.fulfilled, (state, action) => {
        state.checkupList = action.payload;
      })

      .addCase(getCountriesList.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(getCountryDetails.fulfilled, (state, action) => {
        state.countryDetails = action.payload;
      })

      .addCase(getTestSections.fulfilled, (state, action) => {
        state.testSections = action.payload;
      })
      .addCase(getTestProgress.fulfilled, (state, action) => {
        state.testProgress = action.payload;
      })
      .addCase(submitTestAnswer.fulfilled, (state) => {
        // Answer submitted, progress will be updated on next fetch
      })
      .addCase(getTestResults.fulfilled, (state, action) => {
        state.testResults = action.payload;
      })
      .addCase(completeTest.fulfilled, (state) => {
        // Test completed
        if (state.testProgress) {
          state.testProgress.is_completed = true;
        }
      })
      .addCase(resetTest.fulfilled, (state) => {
        // Test reset - clear progress
        state.testProgress = null;
        state.testResults = null;
      });
  },
});

export const { setLang } = infoSlice.actions;

export default infoSlice.reducer;
